/**
 * Section B6 Page - B.6.逆轉風險
 * 四頁籤各一富文本，各頁籤個別儲存/重置
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import sectionB6Service, { SectionB6Update } from '../services/sectionB6Service';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const FIELDS = ['risk_identification', 'risk_assessment', 'risk_mitigation_plan', 'risk_remediation'] as const;
type FieldKey = typeof FIELDS[number];

const SectionB6Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProjectId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);
  const isZh = i18n.language === 'zh-TW';

  const [activeTab, setActiveTab] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<FieldKey, string>>({ risk_identification: '', risk_assessment: '', risk_mitigation_plan: '', risk_remediation: '' });
  const [originalData, setOriginalData] = useState<Record<FieldKey, string>>({ ...formData });

  const tabConfig = [
    { key: 'risk_identification' as FieldKey, label: isZh ? 'B.6.1.回復風險之辨識' : 'B.6.1. Identification of risk of reversal', fieldLabel: isZh ? '風險說明' : 'Risk Identification', placeholder: isZh ? '請輸入風險說明...' : 'Please enter risk identification...' },
    { key: 'risk_assessment' as FieldKey, label: isZh ? 'B.6.2.回復風險評估' : 'B.6.2. Reversals risk assessment', fieldLabel: isZh ? '風險評估' : 'Risk Assessment', placeholder: isZh ? '請輸入風險評估...' : 'Please enter risk assessment...' },
    { key: 'risk_mitigation_plan' as FieldKey, label: isZh ? 'B.6.3.風險緩解' : 'B.6.3. Reversals risk mitigation plan', fieldLabel: isZh ? '風險緩解計畫' : 'Risk Mitigation Plan', placeholder: isZh ? '請輸入風險緩解計畫...' : 'Please enter risk mitigation plan...' },
    { key: 'risk_remediation' as FieldKey, label: isZh ? 'B.6.4.逆轉補救' : 'B.6.4. Remediation of reversals', fieldLabel: isZh ? '風險補救機制' : 'Risk Remediation', placeholder: isZh ? '請輸入風險補救機制...' : 'Please enter risk remediation...' }
  ];

  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) { validateSession('SectionB6Page'); hasInitialized.current = true; }
  }, [permissionLoading]);

  useEffect(() => {
    if (!selectedProjectId) { const empty = { risk_identification: '', risk_assessment: '', risk_mitigation_plan: '', risk_remediation: '' }; setFormData(empty); setOriginalData(empty); return; }
    const loadData = async () => {
      try {
        setLoading(true); setError(null);
        const data = await sectionB6Service.getSectionB6ByProject(selectedProjectId);
        if (data) {
          const values: Record<FieldKey, string> = { risk_identification: data.risk_identification || '', risk_assessment: data.risk_assessment || '', risk_mitigation_plan: data.risk_mitigation_plan || '', risk_remediation: data.risk_remediation || '' };
          setFormData(values); setOriginalData(values);
        } else {
          const empty = { risk_identification: '', risk_assessment: '', risk_mitigation_plan: '', risk_remediation: '' };
          setFormData(empty); setOriginalData(empty);
        }
      } catch (err: any) {
        if (err.response?.status === 404 || err.response?.status === 500) {
          const empty = { risk_identification: '', risk_assessment: '', risk_mitigation_plan: '', risk_remediation: '' };
          setFormData(empty); setOriginalData(empty);
        } else { setError(err.response?.data?.detail || (isZh ? '載入資料失敗' : 'Failed to load data')); }
      } finally { setLoading(false); }
    };
    loadData();
  }, [selectedProjectId, isZh]);

  const currentField = tabConfig[activeTab];

  const handleSave = async () => {
    if (!selectedProjectId) return;
    try {
      setLoading(true); setError(null);
      const updateData: SectionB6Update = { [currentField.key]: formData[currentField.key] || undefined };
      const updated = await sectionB6Service.updateSectionB6(selectedProjectId, updateData);
      const val = (updated as any)[currentField.key] || '';
      setOriginalData(prev => ({ ...prev, [currentField.key]: val }));
      toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) { toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed')); }
    finally { setLoading(false); }
  };

  const handleReset = () => { setFormData(prev => ({ ...prev, [currentField.key]: originalData[currentField.key] })); setError(null); };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('section_b6', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canEdit = hasPermission('section_b6', 'update');

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="section_b6" />
      </div>
      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}
      {selectedProjectId && (
        <div>
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '16px', flexWrap: 'wrap' }}>
            {tabConfig.map((tab, idx) => (
              <button key={idx} onClick={() => setActiveTab(idx)} style={{
                padding: '10px 16px', border: 'none',
                borderBottom: activeTab === idx ? '3px solid #4A90E2' : '3px solid transparent',
                background: activeTab === idx ? '#f0f7ff' : 'transparent',
                color: activeTab === idx ? '#4A90E2' : '#666',
                fontWeight: activeTab === idx ? 600 : 400,
                cursor: 'pointer', fontSize: '13px', marginBottom: '-2px'
              }}>{tab.label}</button>
            ))}
          </div>
          {loading ? <div className="loading">{t('common.loading')}</div> : (
            <>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>{currentField.fieldLabel}</label>
                <RichTextEditor
                  value={formData[currentField.key]}
                  onChange={html => setFormData(prev => ({ ...prev, [currentField.key]: html }))}
                  placeholder={currentField.placeholder}
                  disabled={!canEdit}
                />
              </div>
              {canEdit && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading}>{loading ? t('message.saving') : t('common.save')}</button>
                  <button className="btn-secondary" onClick={handleReset} disabled={loading}>{t('common.reset')}</button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {!selectedProjectId && !loading && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {isZh ? '請先在上方選擇專案' : 'Please select a project from the top bar'}
        </div>
      )}
    </div>
  );
};

export default SectionB6Page;
